import * as React from 'react';
import { Home, MessageSquare, LayoutGrid, CheckCircle, Bot, Search, ChevronDown } from 'lucide-react';
import './BottomNav.css';

export type NavTab = 'review' | 'editor' | 'agent' | 'ask' | 'clauses' | 'proofread';

interface BottomNavProps {
  activeTab: NavTab;
  onTabChange: (tab: NavTab) => void;
}

export const BottomNav: React.FC<BottomNavProps> = ({ activeTab, onTabChange }) => {
  const [showEditorMenu, setShowEditorMenu] = React.useState(false);
  const editorMenuRef = React.useRef<HTMLDivElement | null>(null);

  const centerMode: 'editor' | 'agent' | 'ask' =
    activeTab === 'ask' ? 'ask' : activeTab === 'agent' ? 'agent' : 'editor';

  const centerIcon =
    centerMode === 'ask' ? Search : centerMode === 'agent' ? Bot : MessageSquare;
  const centerLabel =
    centerMode === 'ask' ? 'Ask' : centerMode === 'agent' ? 'Agent' : 'Editor';

  React.useEffect(() => {
    if (!showEditorMenu) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (!editorMenuRef.current) return;
      if (!editorMenuRef.current.contains(event.target as Node)) {
        setShowEditorMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEditorMenu]);

  const handleAgentClick = () => {
    onTabChange('agent');
    setShowEditorMenu(false);
  };

  return (
    <div className="bottom-nav">
      <button
        className={`nav-item ${activeTab === 'review' ? 'active' : ''}`}
        onClick={() => onTabChange('review')}
      >
        <Home className="nav-icon" size={20} />
        <span className="nav-label">Review</span>
      </button>
      <div className="nav-item-group" ref={editorMenuRef}>
        {showEditorMenu && (
          <div className="nav-dropdown-menu" role="menu" aria-label="Editor options">
            <button
              className="nav-dropdown-item"
              onClick={() => {
                onTabChange('editor');
                setShowEditorMenu(false);
              }}
              role="menuitem"
            >
              <MessageSquare className="nav-dropdown-icon" size={14} />
              <span>Editor</span>
            </button>
            <button
              className="nav-dropdown-item"
              onClick={handleAgentClick}
              role="menuitem"
            >
              <Bot className="nav-dropdown-icon" size={14} />
              <span>Agent</span>
            </button>
            <button
              className="nav-dropdown-item"
              onClick={() => {
                onTabChange('ask');
                setShowEditorMenu(false);
              }}
              role="menuitem"
            >
              <Search className="nav-dropdown-icon" size={14} />
              <span>Ask</span>
            </button>
          </div>
        )}
        <button
          className={`nav-item ${activeTab === 'editor' || activeTab === 'agent' || activeTab === 'ask' ? 'active' : ''}`}
          onClick={() => setShowEditorMenu((prev) => !prev)}
          aria-expanded={showEditorMenu}
          aria-haspopup="menu"
        >
          {React.createElement(centerIcon, { className: 'nav-icon', size: 20 })}
          <span className="nav-label-with-caret">
            <span className="nav-label">{centerLabel}</span>
            <ChevronDown className={`nav-caret ${showEditorMenu ? 'open' : ''}`} size={12} />
          </span>
        </button>
      </div>
      <button
        className={`nav-item ${activeTab === 'clauses' ? 'active' : ''}`}
        onClick={() => onTabChange('clauses')}
      >
        <LayoutGrid className="nav-icon" size={20} />
        <span className="nav-label">Solutions</span>
      </button>
      <button
        className={`nav-item ${activeTab === 'proofread' ? 'active' : ''}`}
        onClick={() => onTabChange('proofread')}
      >
        <CheckCircle className="nav-icon" size={20} />
        <span className="nav-label">Proofread</span>
      </button>
    </div>
  );
};

